/**
 * Detects DOM nodes injected by password/credential-manager browser extensions
 * (1Password, Dashlane, LastPass, Bitwarden). Their popups live in the page DOM
 * (often behind a shadow root, so the retargeted event target is the host
 * element). A modal that treats every outside pointerdown as "dismiss" also
 * calls preventDefault to keep focus, which swallows the click before the
 * extension popup can act on it — so its "Save one-time password" button becomes
 * unclickable. We use this to let those interactions pass through untouched.
 */

// Custom-element host tag prefixes used by the extensions' injected UIs.
const HOST_TAG_PREFIXES = ['COM-1PASSWORD', 'DASHLANE-', 'BW-'];

const HOST_SELECTOR = [
  '[data-1p-root]',
  '[data-1password-root]',
  'com-1password-notification',
  'com-1password-menu',
  '#lastpass-overlay',
  '[data-lastpass-root]',
  '[data-dashlane-rid]',
  '[data-dashlanecreated]',
].join(',');

export const isCredentialManagerElement = (element: Element | null): boolean => {
  for (let node = element; node; node = node.parentElement) {
    if (HOST_TAG_PREFIXES.some((prefix) => node.tagName.startsWith(prefix))) return true;
    if (node.matches(HOST_SELECTOR)) return true;
  }
  return false;
};

/**
 * react-aria modals hide everything outside the dialog with `inert` (and a
 * MutationObserver that re-hides nodes injected while the modal is open). That
 * makes extension popups (1Password's "Save one-time password", inserted into
 * the page after the modal opens) non-interactive. This clears `inert` /
 * `aria-hidden` on those hosts and tags them with `data-react-aria-top-layer`,
 * which react-aria treats as always-visible, so they stay clickable.
 */
export const revealCredentialManagers = (): void => {
  document.querySelectorAll<HTMLElement>(HOST_SELECTOR).forEach((element) => {
    if (element.inert) element.inert = false;
    if (element.getAttribute('aria-hidden') === 'true') element.removeAttribute('aria-hidden');
    element.dataset.reactAriaTopLayer = 'true';
  });
};
